import { ImageWithPlaceHolder } from "@/components/Image/WithPlaceHolder";
import { SubWithTitle } from "@/components/SubPanel/WithTitle";
import TextArea from "@/components/TextArea";
import { InfoText, LabelText } from "@/modules/editor/components/styleComponents/Texts";
import { MODEL_TYPES_INFO } from "@/modules/editor/constant";
import useImageWithPlaceholder from "@/modules/editor/hooks/useImageWithPlaceholder";
import { setNegativePrompt, setTxt2ImgTexts, statusStore, updateStatus } from "@/stores/status";
import { Actions } from "@/utils/api";
import { useTranslate } from "@/utils/lang";
import { Editor_Words } from "@/utils/lang/editor";
import { toast, toFixed, useOwnToast } from "@/utils/misc";
import { isVip } from "@/utils/status";
import { Center, Flex } from "@chakra-ui/react";
import { BoardStore, langStore } from "@noli/business";
import { useUnmount } from "ahooks";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";
import BizGenerateButton from "@/components/bussiness/BizGenerateButton";
import { GeneratePriceRules } from "@/modules/editor/hooks/useGenerateCost";
import { GenerateBizType } from "@/types/creator";
import { RadioOptionType } from "@/components/Radio/RadioTag";
import { radioTagItemStyle } from "@/theme/util";

const OUTPAINTING_MODE_OPTIONS: (RadioOptionType<0 | 1> & { bizType: GenerateBizType })[] = [
  {
    value: 0,
    label: "稳定",
    bizType: GenerateBizType.OutpaintingPipline,
  },
  {
    value: 1,
    label: "创新",
    bizType: GenerateBizType.Outpainting,
  },
];
const IOutpaintingPanel: React.FC = () => {
  const { translate } = useTranslate();
  const {
    txt2imgTexts: text,
    negativePrompt,
    outpaintingAreaAlias,
    outpaintingOverlapped,
    outpaintingSrc,
    outpaintingReady,
    txt2imgTexts,
  } = statusStore;
  const lang = langStore.tgt;
  const toastFn = useOwnToast();
  const [outpaintingMode, setOutpaintingMode] = useState<0 | 1>(0);
  const outpaintingModeConfig = useMemo(
    () => OUTPAINTING_MODE_OPTIONS[outpaintingMode],
    [outpaintingMode],
  );
  console.log("outpaintingModeConfig: ", outpaintingModeConfig);

  const { enable, alias, placeholder } = useImageWithPlaceholder({
    type: "rectangle",
    placeholderConfig: {
      noneSelect: Editor_Words["select-a-blank-node"],
      notTargetType: Editor_Words["current-select-not-blank-node"],
    },
  });
  const outpaintingNode = outpaintingAreaAlias
    ? BoardStore.graph.getNode(outpaintingAreaAlias)
    : undefined;
  const wh = useMemo(() => {
    const wh = outpaintingNode?.bbox.bounding.toAABB();
    if (wh)
      return {
        w: toFixed(wh.w, 2),
        h: toFixed(Math.abs(wh.h), 2),
      };
    return { w: 0, h: 0 };
  }, [outpaintingNode]);

  useEffect(() => {
    updateStatus("outpaintingAreaAlias", alias);
  }, [alias]);

  useUnmount(() => {
    updateStatus("outpaintingAreaAlias", undefined);
  });

  async function onOutpainting(): Promise<void> {
    // sanity check
    if (!text) {
      toast(toastFn, "warning", translate(Editor_Words["no-text-input-message"]));
      return;
    }
    const alias = outpaintingAreaAlias;
    if (!alias) {
      toast(toastFn, "warning", translate(Editor_Words["no-selecting-node-message"]));
      return;
    }

    const node = BoardStore.graph.getNode(alias);
    if (node?.type !== "rectangle") return;
    const overlapped = outpaintingOverlapped;

    if (!overlapped) return;
    if (overlapped.length === 0) {
      toast(toastFn, "warning", translate(Editor_Words["no-overlap-error-message"]));
      return;
    }

    // outpainting
    await Actions.outpaintingWith({
      t: toastFn,
      lang,
      bounding: node.bbox.bounding.toAABB(),
      areaNode: node,
      overlappedNodes: overlapped,
      opt: {
        text,
        negativePrompt,
      },
      noToast: false,
      checkBusy: !isVip(),
      bizType: outpaintingModeConfig.bizType,
    });
  }

  return (
    <SubWithTitle h="100%" title={translate(Editor_Words["outpainting"])} pos="relative">
      <LabelText mt="16px">{translate(Editor_Words["outpainting-preview-title"])}：</LabelText>
      <ImageWithPlaceHolder url={outpaintingSrc} placeholderText={placeholder} />
      <LabelText>{translate(Editor_Words["outpainting-text-title"])}：</LabelText>
      <TextArea
        w="100%"
        mb="16px"
        lang={lang}
        textAreaH={300}
        minRows={5}
        maxRows={10}
        placeholderWord={Editor_Words["textarea-placeholder"]}
        text={txt2imgTexts}
        setText={setTxt2ImgTexts}
      />
      <LabelText>{translate(Editor_Words["negaprompt-input"])}：</LabelText>
      <TextArea
        w="100%"
        mb="16px"
        lang={lang}
        textAreaH={120}
        placeholderWord={Editor_Words["negative-prompt-placeholder"]}
        text={negativePrompt}
        setText={setNegativePrompt}
      />
      <Flex alignItems="center" mb="16px">
        <LabelText whiteSpace="nowrap" color="#FFFFFF" fontWeight={500}>
          模式：
        </LabelText>
        <Flex alignItems="center">
          {OUTPAINTING_MODE_OPTIONS.map((option) => (
            <Center
              key={option.value}
              {...radioTagItemStyle(option.value === outpaintingMode)}
              _notLast={{ mr: "8px" }}
              onClick={() => setOutpaintingMode(option.value)}
            >
              {option.label}
            </Center>
          ))}
        </Flex>
      </Flex>
      {enable ? (
        <InfoText fontSize="14px" mt="16px">
          <LabelText>{translate(Editor_Words["size"])}：</LabelText>
          {translate(Editor_Words["stay-in-step-with-selected-pic"])}
        </InfoText>
      ) : null}
      {enable ? (
        <Flex flexDirection="column" gap="12px" mt="16px">
          <InfoText>
            {translate(Editor_Words["model"])}：{translate(MODEL_TYPES_INFO["nolibox"]?.showInfo)}
          </InfoText>
          <InfoText>
            {translate(Editor_Words["resolution"])}： {wh.w} × {wh.h} px
          </InfoText>
        </Flex>
      ) : null}
      <BizGenerateButton
        sticky
        mt="48px"
        pb="32px"
        bottom="0px"
        size={wh}
        generateCount={1}
        checkBusyConfig={{
          busyAlias: outpaintingAreaAlias,
          checkVip: true,
        }}
        calcPriceProps={GeneratePriceRules[outpaintingModeConfig.bizType]}
        buttonProps={{
          isDisabled: !outpaintingReady,
        }}
        onSubmit={onOutpainting}
      >
        {translate(Editor_Words["outpainting-submit"])}
      </BizGenerateButton>
    </SubWithTitle>
  );
};

export const OutpaintingPanel = observer(IOutpaintingPanel);
